import React, { useMemo } from 'react';
import { PhotoGallery } from '../PhotoGallery';
import { LayoutSection } from '../Layout/LayoutSection';
import type { ReportGallerySectionsProps, GalleryGroup } from './ReportGallerySections.types';
import type { IconName } from '../SVGIcon/SVGIcon.types';

const GALLERY_CONFIG: Record<
  string,
  { title: string; icon: IconName; order: number }
> = {
  interior: {
    title: 'Galeria wnętrza pojazdu',
    icon: 'trophy',
    order: 1,
  },
  undercarriage: {
    title: 'Galeria podwozia pojazdu',
    icon: 'network-nodes',
    order: 2,
  },
  body: {
    title: 'Galeria nadwozia pojazdu',
    icon: 'car',
    order: 3,
  },
  engineCompartment: {
    title: 'Galeria komory silnika',
    icon: 'handshake',
    order: 4,
  },
  diagnostics: {
    title: 'Galeria diagnostyki',
    icon: 'monitor-screen',
    order: 5,
  },
  documentation: {
    title: 'Galeria historii napraw',
    icon: 'wrench-tool',
    order: 6,
  },
  other: {
    title: 'Pozostałe zdjęcia',
    icon: 'grid-squares',
    order: 7,
  },
};

export const ReportGallerySections: React.FC<ReportGallerySectionsProps> = ({
  galleries,
}) => {
  const galleryGroups = useMemo<GalleryGroup[]>(() => {
    const groups: GalleryGroup[] = [];

    const galleryKeys = [
      'interior',
      'undercarriage',
      'body',
      'engineCompartment',
      'diagnostics',
      'documentation',
      // 'other',
    ] as const;

    for (const key of galleryKeys) {
      const photos = galleries[key];
      if (photos && photos.length > 0) {
        const config = GALLERY_CONFIG[key];
        groups.push({
          id: key,
          title: config.title,
          icon: config.icon,
          photos,
        });
      }
    }

    return groups.sort((a, b) => {
      const orderA = GALLERY_CONFIG[a.id]?.order ?? 99;
      const orderB = GALLERY_CONFIG[b.id]?.order ?? 99;
      return orderA - orderB;
    });
  }, [galleries]);

  const hasAnyGroupedGalleries = galleryGroups.length > 0;
  const hasAllPhotos = galleries.all.length > 0;

  if (!hasAllPhotos) {
    return null;
  }

  return (
    <LayoutSection
      title="Galeria"
      description="Dokumentacja fotograficzna pojazdu oraz stanu jego poszczególnych elementów."
      icon="grid-squares"
      id="gallery"
    >
      <div className="flex flex-col gap-0">
        {/* Główna galeria - wszystkie zdjęcia */}
        {
          galleries.other.length > 0 &&
          <PhotoGallery
            photos={galleries.other}
            title="Galeria główna"
            collapsible={galleries.other.length > 8}
            defaultExpanded={true}
            galleryVariant="default"
            previewCount={8}
            showIcon={false}
          />
        }

        {/* Podgalerie według kategorii */}
        {hasAnyGroupedGalleries && (
          <div className="flex flex-col gap-0 mt-10">
            {galleryGroups.map((group) => (
              <PhotoGallery
                key={group.id}
                title={group.title}
                iconName={group.icon}
                photos={group.photos}
                collapsible={group.photos.length > 8}
                defaultExpanded={false}
                galleryVariant="default"
                previewCount={8}
              />
            ))}
          </div>
        )}
      </div>
    </LayoutSection>
  );
};
